import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../../firebase/config";
import { doc, onSnapshot, updateDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

export default function DetallePaciente() {
  const { id } = useParams();
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [paciente, setPaciente] = useState(null);
  const [editando, setEditando] = useState(false);
  const [datos, setDatos] = useState({});
  const [tabActiva, setTabActiva] = useState('datos');
  const [historialTurnos, setHistorialTurnos] = useState([]);
  const [loadingTurnos, setLoadingTurnos] = useState(false);

  const canEdit = ['gestion_general', 'recepcion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pacientes', id), snap => {
      if (snap.exists()) { setPaciente({ id: snap.id, ...snap.data() }); setDatos({ ...snap.data() }); }
    });
    return unsub;
  }, [id]);

  const [presupuestosP, setPresupuestosP] = useState([]);
  const [financiacionesP, setFinanciacionesP] = useState([]);

  useEffect(() => {
    if (!paciente?.dni || !clinicaId) return;
    setLoadingTurnos(true);

    const qTurnos = query(collection(db, 'turnos'), where('dni', '==', paciente.dni), where('clinicaId', '==', clinicaId), orderBy('fecha', 'desc'));
    getDocs(qTurnos).then(snap => { setHistorialTurnos(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadingTurnos(false); });

    const qPresupuestos = query(collection(db, 'presupuestos'), where('pacienteDni', '==', paciente.dni), where('clinicaId', '==', clinicaId), orderBy('creadoEn', 'desc'));
    getDocs(qPresupuestos).then(snap => setPresupuestosP(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const qFinanciaciones = query(collection(db, 'financiaciones'), where('pacienteDni', '==', paciente.dni), where('clinicaId', '==', clinicaId), where('tipo', '==', 'cliente'), orderBy('creadoEn', 'desc'));
    getDocs(qFinanciaciones).then(snap => setFinanciacionesP(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

  }, [paciente?.dni, clinicaId]);

  async function guardarCambios() {
    await updateDoc(doc(db, 'pacientes', id), datos);
    await registrarAccion(currentUser?.email, userRole, 'Pacientes', 'Editar paciente', `${datos.apellido}, ${datos.nombre}`, clinicaId);
    setEditando(false);
  }

  if (!paciente) return <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>Cargando...</div>;

  const tipoColor = { consulta: theme.infoBg, control: theme.successBg, estudio: theme.warningBg, cirugia: theme.errorBg };
  const tipoTextColor = { consulta: theme.accent, control: theme.success, estudio: theme.warning, cirugia: theme.error };
  const tipoLabel = { consulta: 'Consulta', control: 'Control', estudio: 'Estudio', cirugia: 'Cirugía' };
  const estadoColor = { atendido: theme.successBg, espera: theme.warningBg, consultorio: theme.warningBg, programado: theme.bgCard, cancelado: theme.errorBg, ausente: theme.warningBg };
  const estadoTextColor = { atendido: theme.success, espera: theme.warning, consultorio: theme.warning, programado: theme.secondary, cancelado: theme.error, ausente: theme.warning };
  const estadoLabel = { atendido: 'Atendido', espera: 'En sala', consultorio: 'En consultorio', programado: 'Programado', cancelado: 'Cancelado', ausente: 'Ausente' };

  const renderTurno = t => {
    const fecha = t.fecha?.toDate?.();
    const fechaStr = fecha ? fecha.toLocaleDateString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '—';
    const horaStr = fecha ? `${fecha.getHours().toString().padStart(2,'0')}:${fecha.getMinutes().toString().padStart(2,'0')}` : '—';
    return (
      <div key={t.id} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', border: `0.5px solid ${theme.border}` }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{fechaStr} — {horaStr}</div>
          <div style={{ fontSize: '12px', color: theme.secondary, marginTop: '2px' }}>{t.medico === 'medico1' ? (clinicaData?.medicos?.medico1 || 'Médico 1') : (clinicaData?.medicos?.medico2 || 'Médico 2')}{t.detalle ? ` · ${t.detalle}` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: tipoColor[t.tipo] || theme.bgCard, color: tipoTextColor[t.tipo] || theme.secondary, fontWeight: '500' }}>{tipoLabel[t.tipo] || t.tipo}</span>
          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: estadoColor[t.estado] || theme.bgCard, color: estadoTextColor[t.estado] || theme.secondary, fontWeight: '500' }}>{estadoLabel[t.estado] || t.estado}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo={`${paciente.apellido}, ${paciente.nombre}`} backLabel="Pacientes" backRuta="/pacientes" />
      <div style={{ padding: '12px 24px 0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: theme.bg }}>
        {canEdit && !editando && <button onClick={() => setEditando(true)} style={btnSecondaryStyle}>Editar datos</button>}
        {editando && <>
          <button onClick={guardarCambios} style={btnPrimaryStyle}>Guardar</button>
          <button onClick={() => { setEditando(false); setDatos(paciente); }} style={btnSecondaryStyle}>Cancelar</button>
        </>}
      </div>

      <div style={{ padding: '16px 24px', background: theme.bg }}>
        {paciente.alergia && (
          <div style={{ background: theme.errorBg, border: `0.5px solid ${theme.error}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: theme.error, fontWeight: '500' }}>
            ⚠ ALERGIA: {paciente.detalleAlergia}
          </div>
        )}

        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', borderBottom: `0.5px solid ${theme.border}`, paddingBottom: '12px' }}>
          {[
            { id: 'datos', label: 'Datos del paciente' },
            { id: 'historia', label: 'Historia Clínica' },
            { id: 'historial', label: 'Historial de turnos' },
          ].map(tab => (
            <button key={tab.id} onClick={() => { if (tab.id === 'historia') navigate(`/pacientes/${id}/historia`); else setTabActiva(tab.id); }} style={{
              padding: '7px 14px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer',
              border: 'none',
              background: tabActiva === tab.id ? theme.accent : 'transparent',
              color: tabActiva === tab.id ? '#FFFFFF' : theme.secondary,
              fontFamily: "'Montserrat', sans-serif", fontWeight: tabActiva === tab.id ? '600' : '400'
            }}>
              {tab.label}
            </button>
          ))}
        </div>

        {tabActiva === 'datos' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {[
              { label: 'APELLIDO', key: 'apellido' },
              { label: 'NOMBRE', key: 'nombre' },
              { label: 'DNI', key: 'dni' },
              { label: 'NACIONALIDAD', key: 'nacionalidad' },
              { label: 'FECHA DE NACIMIENTO', key: 'fechaNacimiento', type: 'date' },
              { label: 'SEXO', key: 'sexo' },
              { label: 'EMAIL', key: 'email', type: 'email' },
              { label: 'TELÉFONO', key: 'telefono' },
              { label: 'COBERTURA', key: 'cobertura' },
              { label: 'N° AFILIADO', key: 'numeroAfiliado' },
            ].map(campo => (
              <div key={campo.key}>
                <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500', letterSpacing: '1px' }}>{campo.label}</div>
                {editando
                  ? <input type={campo.type || 'text'} value={datos[campo.key] || ''} onChange={e => setDatos({...datos, [campo.key]: e.target.value})} style={inputStyle} />
                  : <div style={{ fontSize: '14px', color: theme.primary, padding: '8px 0' }}>{paciente[campo.key] || '—'}</div>
                }
              </div>
            ))}
            {paciente.esMenor && (
              <>
                <div style={{ gridColumn: '1 / -1', borderTop: `0.5px solid ${theme.border}`, paddingTop: '12px', fontSize: '12px', fontWeight: '600', color: theme.accent, letterSpacing: '1px' }}>DATOS DEL TUTOR</div>
                {[
                  { label: 'NOMBRE', key: 'tutorNombre' },
                  { label: 'DNI', key: 'tutorDni' },
                  { label: 'RELACIÓN', key: 'tutorRelacion' },
                  { label: 'EMAIL', key: 'tutorEmail' },
                  { label: 'TELÉFONO', key: 'tutorTelefono' },
                ].map(campo => (
                  <div key={campo.key}>
                    <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500', letterSpacing: '1px' }}>{campo.label}</div>
                    {editando
                      ? <input value={datos[campo.key] || ''} onChange={e => setDatos({...datos, [campo.key]: e.target.value})} style={inputStyle} />
                      : <div style={{ fontSize: '14px', color: theme.primary, padding: '8px 0' }}>{paciente[campo.key] || '—'}</div>
                    }
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tabActiva === 'historial' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {loadingTurnos
              ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '32px', fontSize: '13px' }}>Cargando historial...</div>
              : <>
                  {(() => {
                    const ahora = new Date();
                    const proximos = historialTurnos.filter(t => t.fecha?.toDate?.() >= ahora && t.estado !== 'cancelado').reverse();
                    const pasados = historialTurnos.filter(t => t.fecha?.toDate?.() < ahora || t.estado === 'cancelado' || t.estado === 'ausente');
                    return (
                      <>
                        {proximos.length > 0 && (
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: theme.accent, marginBottom: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Próximos turnos ({proximos.length})</div>
                            {proximos.map(renderTurno)}
                          </div>
                        )}
                        {pasados.length > 0 && (
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: theme.secondary, marginBottom: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Historial de turnos ({pasados.length})</div>
                            {pasados.map(renderTurno)}
                          </div>
                        )}
                        {historialTurnos.length === 0 && <div style={{ textAlign: 'center', color: theme.secondary, padding: '16px', fontSize: '13px', background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>No hay turnos registrados</div>}
                      </>
                    );
                  })()}

                  {presupuestosP.length > 0 && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: theme.accent, marginBottom: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Presupuestos ({presupuestosP.length})</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {presupuestosP.map(p => {
                          const ESTADOS_P = { borrador: { label: 'Borrador', color: theme.secondary }, enviado: { label: 'Enviado', color: theme.accent }, aceptado: { label: 'Aceptado', color: theme.success }, rechazado: { label: 'Rechazado', color: theme.error } };
                          const est = ESTADOS_P[p.estado] || { label: p.estado, color: theme.secondary };
                          const fecha = p.creadoEn?.toDate ? p.creadoEn.toDate().toLocaleDateString('es-AR') : '—';
                          const conceptos = [p.incluirEstudios && 'Estudios', p.incluirTratamientos && 'Tratamientos', p.incluirCirugias && 'Cirugías'].filter(Boolean).join(' · ') || '—';
                          return (
                            <div key={p.id} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{conceptos}</div>
                                <div style={{ fontSize: '11px', color: theme.secondary }}>{fecha}{p.detalle ? ` · ${p.detalle.substring(0,40)}` : ''}</div>
                              </div>
                              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: `${est.color}20`, color: est.color, fontWeight: '500', flexShrink: 0 }}>{est.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {financiacionesP.length > 0 && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: theme.accent, marginBottom: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Financiaciones ({financiacionesP.length})</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {financiacionesP.map(f => {
                          const pct = f.montoTotal > 0 ? Math.min(((f.totalPagado || 0) / f.montoTotal) * 100, 100).toFixed(0) : 0;
                          return (
                            <div key={f.id} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${theme.border}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{f.concepto}</div>
                                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: f.estado === 'completada' ? theme.successBg : theme.infoBg, color: f.estado === 'completada' ? theme.success : theme.accent, fontWeight: '500' }}>
                                  {f.estado === 'completada' ? 'Completada' : 'Activa'}
                                </span>
                              </div>
                              <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '6px' }}>
                                {f.moneda === 'USD' ? 'USD ' : '$'}{parseFloat(f.montoTotal).toLocaleString('es-AR')} · {f.cantidadPeriodos} períodos {f.periodicidad}s
                              </div>
                              <div style={{ height: '4px', background: theme.border, borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: f.estado === 'completada' ? theme.success : theme.accent, borderRadius: '99px' }} />
                              </div>
                              <div style={{ fontSize: '10px', color: theme.secondary, marginTop: '2px' }}>{pct}% pagado</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
            }
          </div>
        )}
      </div>
    </div>
  );
}